import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";
import { TripletexSyncDialog } from "./tripletex-sync-dialog";

const STATE_CONFIG = {
  synced: { cls: "bg-green-100 text-green-800", Icon: CheckCircleIcon, key: "tripletex_sync.badge.synced" },
  blocked: { cls: "bg-amber-100 text-amber-800", Icon: ExclamationTriangleIcon, key: "tripletex_sync.badge.blocked" },
  error: { cls: "bg-red-100 text-red-800", Icon: XCircleIcon, key: "tripletex_sync.badge.error" },
  pending: { cls: "bg-blue-100 text-blue-800", Icon: ClockIcon, key: "tripletex_sync.badge.pending" },
  not_synced: { cls: "bg-amber-100 text-amber-800", Icon: ExclamationTriangleIcon, key: "tripletex_sync.badge.not_synced" },
};

/**
 * Compact Tripletex sync badge for list rows. Renders nothing when the company is not
 * connected to Tripletex. `iconOnly` shows just the colored icon (with tooltip).
 */
export const TripletexSyncBadge = ({
  sync,
  iconOnly = false,
  interactive = false,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!sync || !sync.enabled || sync.state === "disabled") return null;

  const config = STATE_CONFIG[sync.state] || STATE_CONFIG.not_synced;
  const Icon = config.Icon;
  const label = t(config.key);
  const title = sync.title || label;
  const iconClass =
    sync.state === "synced"
      ? "text-green-500"
      : sync.state === "error"
        ? "text-red-500"
        : sync.state === "pending"
          ? "text-blue-500"
          : "text-amber-500";

  const openDialog = (event) => {
    event.stopPropagation();
    setDialogOpen(true);
  };

  const dialog = interactive ? (
    <TripletexSyncDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      sync={sync}
      onRetry={onRetry}
    />
  ) : null;

  if (iconOnly) {
    if (!interactive) {
      return (
        <span title={title}>
          <Icon className={`h-5 w-5 ${iconClass}`} aria-hidden="true" />
        </span>
      );
    }
    return (
      <>
        <button
          type="button"
          title={title}
          aria-label={t("tripletex_sync.dialog.open")}
          onClick={openDialog}
          className="inline-flex rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1463C7]"
        >
          <Icon className={`h-5 w-5 ${iconClass}`} aria-hidden="true" />
        </button>
        {dialog}
      </>
    );
  }

  const className = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.cls}`;

  if (!interactive) {
    return (
      <span title={title} className={className}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        title={title}
        aria-label={t("tripletex_sync.dialog.open")}
        onClick={openDialog}
        className={`${className} cursor-pointer hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1463C7]`}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </button>
      {dialog}
    </>
  );
};

export default TripletexSyncBadge;
